import axios, { AxiosError } from 'axios';

const PROJECT_SERVICE_URL = import.meta.env.VITE_JOB_SERVICE_URL || 'http://localhost:8003';

// Project interface
export interface Project {
    _id?: string;
    projectId: string;
    name: string;
    companyId: string;
    companyName: string;
    createdBy: string;
    jobIds: string[];
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    jobCount?: number;
}

// API Response interfaces
interface ApiResponse<T> {
    payload: T;
    status: {
        msg: string;
        error: boolean;
    };
}

interface PaginatedResponse<T> {
    data: T[];
    pagination: {
        page: number;
        size: number;
        totalPages: number;
        totalItems: number;
    };
}

interface ProjectJobsResponse {
    project: {
        projectId: string;
        name: string;
        companyName: string;
    };
    jobs: any[];
    pagination: {
        page: number;
        size: number;
        totalPages: number;
        totalItems: number;
    };
}

class ProjectApiService {
    private getAuthToken(): string | null {
        return localStorage.getItem('authToken');
    }

    private getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
        };
    }

    /**
     * Create a new project
     */
    async createProject(name: string): Promise<Project> {
        try {
            const authUser = localStorage.getItem('authUser');
            if (!authUser) {
                throw new Error('User not authenticated');
            }

            const user = JSON.parse(authUser);
            const createdBy = user.id; // MongoDB user _id

            const response = await axios.post<ApiResponse<Project>>(
                `${PROJECT_SERVICE_URL}/projects/v1/create`,
                { name, createdBy },
                { headers: this.getAuthHeaders() }
            );

            return response.data.payload;
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to create project');
            }
            throw error;
        }
    }

    /**
     * List all projects for the current user's company
     */
    async listProjects(page: number = 1, size: number = 20): Promise<PaginatedResponse<Project>> {
        try {
            const authUser = localStorage.getItem('authUser');
            if (!authUser) {
                throw new Error('User not authenticated');
            }

            const user = JSON.parse(authUser);
            const userId = user.id; // MongoDB user _id

            const response = await axios.get<ApiResponse<PaginatedResponse<Project>>>(
                `${PROJECT_SERVICE_URL}/projects/v1/list`,
                {
                    params: { userId, page, size },
                    headers: this.getAuthHeaders(),
                }
            );

            return response.data.payload;
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to fetch projects');
            }
            throw error;
        }
    }

    /**
     * Get a single project by ID
     */
    async getProject(projectId: string): Promise<Project> {
        try {
            const response = await axios.get<ApiResponse<Project>>(
                `${PROJECT_SERVICE_URL}/projects/v1/${projectId}`,
                { headers: this.getAuthHeaders() }
            );

            return response.data.payload;
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to fetch project');
            }
            throw error;
        }
    }

    /**
     * Get all jobs in a project
     */
    async getProjectJobs(
        projectId: string,
        page: number = 1,
        size: number = 10
    ): Promise<ProjectJobsResponse> {
        try {
            const response = await axios.get<ApiResponse<ProjectJobsResponse>>(
                `${PROJECT_SERVICE_URL}/projects/v1/${projectId}/jobs`,
                {
                    params: { page, size },
                    headers: this.getAuthHeaders(),
                }
            );

            return response.data.payload;
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to fetch project jobs');
            }
            throw error;
        }
    }

    /**
     * Update a project
     */
    async updateProject(projectId: string, name: string): Promise<void> {
        try {
            await axios.patch(
                `${PROJECT_SERVICE_URL}/projects/v1/${projectId}`,
                { name },
                { headers: this.getAuthHeaders() }
            );
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to update project');
            }
            throw error;
        }
    }

    /**
     * Delete a project (soft delete)
     */
    async deleteProject(projectId: string): Promise<void> {
        try {
            await axios.delete(`${PROJECT_SERVICE_URL}/projects/v1/${projectId}`, {
                headers: this.getAuthHeaders(),
            });
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to delete project');
            }
            throw error;
        }
    }

    /**
     * Add a job to a project
     */
    async addJobToProject(projectId: string, jobId: string): Promise<void> {
        try {
            await axios.post(
                `${PROJECT_SERVICE_URL}/projects/v1/${projectId}/add-job`,
                { job_id: jobId },  // Backend expects snake_case
                { headers: this.getAuthHeaders() }
            );
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to add job to project');
            }
            throw error;
        }
    }

    /**
     * Remove a job from a project
     */
    async removeJobFromProject(projectId: string, jobId: string): Promise<void> {
        try {
            await axios.post(
                `${PROJECT_SERVICE_URL}/projects/v1/${projectId}/remove-job`,
                { job_id: jobId },  // Backend expects snake_case
                { headers: this.getAuthHeaders() }
            );
        } catch (error) {
            if (error instanceof AxiosError) {
                throw new Error(error.response?.data?.error || 'Failed to remove job from project');
            }
            throw error;
        }
    }
}

export const projectApiService = new ProjectApiService();
